// The two APPEND-LOG live views, once (#212 sessions, #59 fleet runs).
//
// Both consume a `backfill → tail → terminate` SSE route whose rows only ever
// grow, and differ in exactly three things: the path, the frame event name and
// the row schema. They were two copies of this hook, which is how they came to
// share one bug — see the `bye` rule below.
//
// The socket, the framing and the reconnect ladder are `lib/sse-subscribe.ts`,
// shared with the multiplexed account stream. What stays here is the Solid
// wiring and the ONE thing an append log does not share with `/v1/streams`: the
// `Last-Event-ID` resume point, and the `announced_only` reconnect posture that
// makes a bare close a fault a human sees rather than a loop that hides it.

import { createEffect, createSignal, onCleanup } from 'solid-js';
import type { z } from 'zod';
import { API_BASE_URL } from './api';
import { mockStreamRows } from './mock-run-stories';
import { MOCKS } from './mocks';
import { urlOrg } from './org';
import { type SseOutcome, sseSubscribe } from './sse-subscribe';
import { DEFAULT_SSE_RETRY_MS } from './sseBackoff';
import { parseFrameData, type StreamStatus } from './sseFrames';

/**
 * WHY the connection is not live, when it is not. `status` collapses a foreign
 * run id, a 503 and a dropped socket into one `error`, so a view rendering it
 * alone can only ever offer a Retry — which, on the 404, retries into the same
 * 404 for ever. `no_session` is the other half: the hook resolves without
 * sending a request at all, and a view that reads only `status` then states
 * "no events for this run" — a claim about a run, made from a request nobody
 * made. Additive and unread by `<AuditSession/>`, which keeps its behaviour.
 */
export type StreamRefusal =
  /** No credential: nothing was asked, so nothing is known. */
  | { readonly kind: 'no_session' }
  /** The server ANSWERED and refused. 404 is an answer, not a fault. */
  | { readonly kind: 'http'; readonly status: number };

export type AppendLogStream<T> = {
  readonly events: () => readonly T[];
  readonly status: () => StreamStatus;
  /** Null while connecting/live/done. See {@link StreamRefusal}. */
  readonly refusal: () => StreamRefusal | null;
  /**
   * Clean-slate reconnect: drops the rows already shown AND the resume point,
   * then re-dials from `now()`. The right answer for a stream that hit a
   * genuine drop the view should treat as over — a server restart leaves a
   * gap that this hook intentionally does NOT close, so any view that wants
   * to recover those rows pairs `retry()` with its own backfill fetch.
   */
  readonly retry: () => void;
  /**
   * In-place reconnect: keeps the rows already shown AND the resume point,
   * then re-dials with `Last-Event-ID` so the new connection picks up exactly
   * where the previous one left off. The right answer for a stream the view
   * wants to keep rendering — a long-lived console handed a fresh socket on
   * a network blip, where losing the rows the user already saw would be the
   * bug.
   */
  readonly redial: () => void;
};

/** What one append-log route needs to say about itself. */
export type AppendLogSpec<T> = {
  /** The `/v1` path for this stream's id. */
  readonly path: (id: string) => string;
  /** The frame `event:` carrying a row (`audit` / `run_event`). */
  readonly event: string;
  /** Row schema — every frame is validated before it reaches the view. */
  readonly schema: z.ZodType<T>;
  /**
   * The most rows `events()` may hold. Omit it for a stream whose real
   * lifetime IS the unit of work — a session or a fleet run ends, and
   * `<AuditSession/>`/`<FleetAudit/>` render (and export) the WHOLE thing, so
   * capping it there would silently truncate what a finished run's audit
   * trail exports.
   *
   * Set it for a stream with no such end — the Terminal org console
   * (`terminal-stream.ts`) is the first caller whose real lifetime is a
   * working day, not one task/run
   * (`docs/standards/long-running-resources.md`: "a short-lived helper given
   * a boot-lived lifetime"). Bounded here, at the primitive, per that doc's
   * Rule 1 — not at the one call site that hit it first.
   */
  readonly retain?: number;
};

/**
 * The credential-less and mock resolutions, which never touch the network.
 *
 * MOCKS serves the story rows for this address (#1981); `token === null` keeps
 * resolving to NOTHING, which is a different statement and the one the
 * no-session cases assert. Without this, `/fleet/runs/:run_id` could never
 * resolve a task id under mocks and the stop control #1906 shipped there was
 * undrivable — it is read off `events()[0].task_id`.
 *
 * Rows go through `spec.schema`, the same validation a real frame gets, so a
 * fixture that drifts from the wire contract fails loudly here instead of
 * half-rendering. Reactive on `id()`: Solid Router keeps this route mounted
 * across param changes, so a static read would strand the previous run's rows.
 */
function offlineStream<T>(
  id: () => string,
  spec: AppendLogSpec<T>,
  set: {
    setEvents: (rows: readonly T[]) => void;
    setStatus: (s: StreamStatus) => void;
    setRefusal: (r: StreamRefusal) => void;
  },
): void {
  set.setStatus('done');
  // A credential-less resolve is REPORTED, never silently dressed as an ended
  // stream. `status` stays `done` so `<AuditSession/>` renders exactly as it
  // did; a view that reads `refusal` says the true thing instead.
  if (!MOCKS) {
    set.setRefusal({ kind: 'no_session' });
    return;
  }
  createEffect(() => {
    set.setEvents(mockStreamRows(spec.path(id())).map((row) => spec.schema.parse(row)));
  });
}

/**
 * The status a STOPPED loop leaves behind, ENUMERATED rather than absorbed.
 *
 * The sibling of `accountStream.ts`'s table of the same name, and it is here for
 * the same reason: the trailing `setStatus('error')` this replaces was a catch-all,
 * so a new `SseOutcome` kind would have been swallowed by it silently — the
 * defect class that shipped one file over as a green `live` pill over a dead
 * socket. A `Record` over the union makes the next kind a compile error naming
 * itself, in BOTH consumers of that union.
 *
 * Every mapping is what this hook already did under `announced_only`, restated
 * as a total map and not re-decided: `ended` is the log reaching its terminal
 * row (`done`), `aborted` is nobody listening (`null` — every signal stays put),
 * and everything else is a fault a human sees behind `<ErrorState/>`'s Retry.
 * `recycled` is unreachable here — `shouldReconnect` reconnects on it under
 * every policy — and reaching it would mean the loop stopped without dialling,
 * which is the same bare fault a close with no announcement is.
 */
const STATUS_AFTER: Record<SseOutcome['kind'], StreamStatus | null> = {
  aborted: null,
  ended: 'done',
  refused: 'error',
  unavailable: 'error',
  dropped: 'error',
  recycled: 'error',
  crashed: 'error',
};

/**
 * Open `spec.path(id())` and expose the accumulating rows, a connection status,
 * a `retry` that reconnects from a clean slate and a `redial` that reconnects
 * in place resuming from the last row seen. `id` is a reactive accessor:
 * Solid Router keeps a route mounted across param changes, so navigating to
 * another session/run tears the old stream down and opens a fresh one. Aborts on
 * cleanup (unmount / id change / org switch / retry / redial).
 */
/** Mutable holder for the resume token — a `let` would not survive being
 *  passed into {@link dialAppendLog}, which both reads it (the `last-event-id`
 *  header) and writes it (a frame carrying `id:`). */
type ResumeRef = { current: string | null };

/**
 * One dial of the SSE connection and its frame handling — split out of
 * {@link useAppendLogStream} to keep that hook under the size-budget function
 * ceiling. Not exported: it has no caller outside this file and no reason to.
 */
function dialAppendLog<T extends { readonly id: string }>(input: {
  spec: AppendLogSpec<T>;
  streamId: string;
  bearer: Record<string, string>;
  orgHeader: Record<string, string>;
  resumeRef: ResumeRef;
  /** Ids already in `events` — held by the hook, so it lives exactly as long as they do. */
  seen: Set<string>;
  controller: AbortController;
  setStatus: (status: StreamStatus) => void;
  setRefusal: (refusal: StreamRefusal | null) => void;
  setEvents: (updater: (prev: readonly T[]) => readonly T[]) => void;
}): void {
  const {
    spec,
    streamId,
    bearer,
    orgHeader,
    resumeRef,
    seen,
    controller,
    setStatus,
    setRefusal,
    setEvents,
  } = input;
  void (async () => {
    // `done` means the log ENDED; `bye` means this connection hit the route's
    // ~10-minute duration cap and the log is still open
    // (`apps/api/src/middleware/sse-stream.ts`). Reading a recycle as an end —
    // or as a drop — is what painted "Disconnected" over a running session
    // every ten minutes, so the recycle is the ONE close this posture
    // reconnects on.
    const outcome = await sseSubscribe({
      policy: { kind: 'announced_only' },
      signal: controller.signal,
      startingRetryMs: DEFAULT_SSE_RETRY_MS,
      dial: () =>
        fetch(`${API_BASE_URL}${spec.path(streamId)}`, {
          credentials: 'include',
          headers: {
            accept: 'text/event-stream',
            ...bearer,
            ...orgHeader,
            // Resume where the previous connection stopped. Without it every
            // recycle re-backfills the whole session, which is why the `bye`
            // handler and this header had to land together: an auto-reconnect
            // that re-reads thousands of rows every ten minutes is worse than
            // the manual button it replaces.
            ...(resumeRef.current !== null ? { 'last-event-id': resumeRef.current } : {}),
          },
          signal: controller.signal,
        }),
      onOpen: () => setStatus('live'),
      onFrames: (frames) => {
        const rows: T[] = [];
        for (const frame of frames) {
          // Only ever OVERWRITTEN by a frame that carries one: the terminal
          // and recycle frames omit `id:` precisely so the token in hand stays
          // the resume point. The write lands on the HOISTED ref, so a
          // `redial()`'s next dial picks it up.
          if (frame.id !== undefined) resumeRef.current = frame.id;
          if (frame.event !== spec.event) continue;
          const row = parseFrameData(spec.schema, frame.data);
          if (!row || seen.has(row.id)) continue;
          seen.add(row.id);
          rows.push(row);
        }
        // ONE append per network chunk. A backfill arrives as a handful of
        // large reads, and a setter per row repainted the whole timeline once
        // per row instead of once per read.
        if (rows.length > 0) {
          setEvents((prev) => {
            const merged = [...prev, ...rows];
            if (spec.retain === undefined || merged.length <= spec.retain) return merged;
            const capped = merged.slice(merged.length - spec.retain);
            // `seen` must stay in lockstep with what `events` actually
            // holds, or IT grows without bound for the same reason `events`
            // did — one more id retained forever per row this route will
            // ever see. Rebuilding it from `capped` can only let a row this
            // old get re-appended if the exact same id arrives again after
            // falling out of the window; this route resumes strictly
            // forward via `Last-Event-ID` (`resumeRef.current`, above), so a
            // frame this stale never does.
            seen.clear();
            for (const row of capped) seen.add(row.id);
            return capped;
          });
        }
      },
      onReconnect: () => setStatus('connecting'),
    });
    const stopped = STATUS_AFTER[outcome.kind];
    if (stopped === null) return;
    // The server ANSWERED, and which status it answered decides whether a
    // Retry is worth offering at all — 404 is an answer and retrying it
    // reproduces it exactly, for ever. Read off the outcome CARRYING a status
    // rather than off a second list of kinds: that list was `refused |
    // unavailable`, which is the same set the union types with a `status`
    // today and one more thing to keep in step tomorrow. A close with neither
    // terminal frame carries none and stays a bare `error` — a stream failing
    // every 3 s is a fault a human should see, not one a loop should hide.
    if ('status' in outcome) setRefusal({ kind: 'http', status: outcome.status });
    setStatus(stopped);
  })();
}

export function useAppendLogStream<T extends { readonly id: string }>(
  id: () => string,
  token: string | null,
  spec: AppendLogSpec<T>,
): AppendLogStream<T> {
  const [events, setEvents] = createSignal<readonly T[]>([]);
  const [status, setStatus] = createSignal<StreamStatus>('connecting');
  const [refusal, setRefusal] = createSignal<StreamRefusal | null>(null);
  // Two triggers, on purpose. `retry()` bumps `retryTrigger` AND clears
  // `events` + `resumeFrom` itself, so the effect re-runs but the gate below
  // skips its own reset (no real `(streamId, org)` change). `redial()` bumps
  // `redialTrigger` only, so the gate skips too — events and `resumeFrom`
  // are kept, and the new dial carries `Last-Event-ID` to pick up where the
  // previous connection left off.
  const [retryTrigger, setRetryTrigger] = createSignal(0);
  const [redialTrigger, setRedialTrigger] = createSignal(0);

  if (token === null || MOCKS) {
    offlineStream(id, spec, { setEvents, setStatus, setRefusal });
    return {
      events,
      status,
      refusal,
      retry: (): void => {},
      redial: (): void => {},
    };
  }
  const bearer = token.startsWith('dev_pat_') ? { authorization: `Bearer ${token}` } : {};

  // Hoisted OUT of the per-effect closure: held across re-runs, so the
  // `Last-Event-ID` on a recycle / redial is the token the previous dial
  // actually reached, not whatever a fresh effect run would default to. A
  // `retry()` and a real `(streamId, org)` change clear this explicitly.
  const resumeRef: ResumeRef = { current: null };
  // The dedupe set, hoisted for the same reason and with the same lifetime as
  // `events`: it was built per DIAL while `events` survived a `redial()`, so a
  // resumed connection re-sending the row at its resume point appended it
  // twice. Cleared exactly where `events` is.
  const seen = new Set<string>();
  // The previous effect run's resource identity. The gate below uses these
  // to decide whether `setEvents([])` should fire — `retry()` and `redial()`
  // both bump a trigger without changing resource, so without this gate the
  // hook would wipe its own `events` on every reconnect it just took
  // trouble to resume.
  let prevStreamId: string | undefined;
  let prevOrg: string | null | undefined;

  createEffect(() => {
    retryTrigger(); // subscribe: retry() bumps this — clean-slate reconnect
    redialTrigger(); // subscribe: redial() bumps this — resume-from-here reconnect
    const streamId = id();
    // Track `urlOrg()` in this scope (#460): an org switch that keeps the same
    // id must still tear the old stream down and reopen under the new account.
    const org = urlOrg();
    // REAL resource change (different id or different org) drops the
    // previous run's rows and clears the resume point — the previous run is
    // over. `retry()` resets BOTH itself before bumping `retryTrigger`, and
    // `redial()` resets NEITHER, so neither lands here: a `retry()` re-dials
    // from `now()`, a `redial()` re-dials with `Last-Event-ID` carried over.
    if (streamId !== prevStreamId || org !== prevOrg) {
      setEvents([]);
      seen.clear();
      resumeRef.current = null;
    }
    prevStreamId = streamId;
    prevOrg = org;
    setStatus('connecting');
    setRefusal(null);
    const controller = new AbortController();
    // URL-derived org context (#460) — same header apiFetch attaches.
    const orgHeader = org !== null ? { 'x-dz-account': org } : {};

    dialAppendLog({
      spec,
      streamId,
      bearer,
      orgHeader,
      resumeRef,
      seen,
      controller,
      setStatus,
      setRefusal,
      setEvents,
    });

    onCleanup(() => controller.abort());
  });

  return {
    events,
    status,
    refusal,
    retry: (): void => {
      // Clean slate: drop the rows already shown AND the resume point, then
      // re-dial from `now()`. Explicit, not delegated to the effect — the
      // gate inside the effect fires on a real resource change, and a
      // `retry()` is intentionally NOT one (no caller has switched the
      // resource they're looking at).
      setEvents([]);
      seen.clear();
      resumeRef.current = null;
      setRetryTrigger((n) => n + 1);
    },
    redial: (): void => {
      // In-place reconnect: keep BOTH the rows already shown AND the resume
      // point — the dial will pass `Last-Event-ID` and resume where the
      // previous connection stopped, so nothing the user already saw has to
      // be replayed, and no gap opens between the last seen id and the new
      // tail.
      setRedialTrigger((n) => n + 1);
    },
  };
}
